import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, ShieldCheck, Sparkles, Clock } from "lucide-react";
import { getPlans, subscribePlan, getStoreInfo } from "../services/api";
import { motion, AnimatePresence, Variants } from "framer-motion";
import { BillingToggle, type BillingCycle } from "../components/pricing/BillingToggle";
import { PricingCard } from "../components/pricing/PricingCard";

export interface PlanItem {
  id: number;
  name: string;
  price: number;
  trial_days: number;
  features: string[];
}

export default function Plans() {
  const queryClient = useQueryClient();
  const [cycle, setCycle] = useState<BillingCycle>("monthly");
  const [subscribingId, setSubscribingId] = useState<number | null>(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const { data: grouped, isLoading } = useQuery({
    queryKey: ["shopify-plans"],
    queryFn: () => getPlans().then((r) => r.data as Record<string, PlanItem[]>),
  });

  const { data: storeInfo } = useQuery({
    queryKey: ["store-settings"],
    queryFn: () => getStoreInfo().then((r) => r.data.store),
  });

  const plans: PlanItem[] = [...(grouped?.[cycle] ?? [])].sort((a, b) => {
    if (a.price === 0 && b.price !== 0) return -1;
    if (b.price === 0 && a.price !== 0) return 1;
    return a.price - b.price;
  });
  const isActive = storeInfo?.plan_status === "active";
  const currentPlanId: number | undefined =
    storeInfo?.plan_id !== undefined && storeInfo?.plan_id !== null
      ? Number(storeInfo.plan_id)
      : undefined;

  const handleChoose = async (plan: PlanItem) => {
    setErr(""); setMsg("");
    setSubscribingId(plan.id);
    try {
      const { data } = await subscribePlan(plan.id, cycle);
      // Free plans activate instantly server-side — nothing to redirect to.
      if (data?.activated) {
        queryClient.invalidateQueries({ queryKey: ["store-settings"] });
        setMsg(`You're now on the ${data.plan_name ?? plan.name} plan.`);
        return;
      }
      if (data?.confirmation_url) {
        const target = window.top ?? window;
        target.location.href = data.confirmation_url;
        return;
      }
      setErr(data?.error || "Could not start checkout for this plan.");
    } catch (e: any) {
      setErr(e?.response?.data?.error || "Could not start checkout for this plan.");
    } finally {
      setSubscribingId(null);
    }
  };

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  const cardVariants: Variants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 26 } },
  };

  return (
    <div className="relative min-h-screen bg-[#F8FAFC] font-['Inter',sans-serif] text-[#0F172A] overflow-x-hidden selection:bg-[#ED5A29]/20 selection:text-[#ED5A29]">
      {/* Background — thoda lighter/tighter kiya */}
      <div className="absolute top-[-15%] left-[25%] w-[50vw] h-[40vw] bg-[#F97316]/8 blur-[110px] rounded-full pointer-events-none z-0" />
      <div className="absolute top-[35%] right-[-10%] w-[35vw] h-[35vw] bg-[#FFF4ED]/40 blur-[90px] rounded-full pointer-events-none z-0" />
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.03] z-0"
        style={{
          backgroundImage: 'linear-gradient(#111827 1px, transparent 1px), linear-gradient(to right, #111827 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          maskImage: 'radial-gradient(ellipse at center, black 35%, transparent 75%)',
          WebkitMaskImage: 'radial-gradient(ellipse at center, black 35%, transparent 75%)'
        }}
      />

      <div className="relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-[28px]">

        {/* Header — compact */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="text-center max-w-2xl mx-auto mb-[30px]"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.15 }}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F97316]/10 text-[#F97316] text-xs font-bold mb-[12px] border border-[#F97316]/20"
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#F97316] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#F97316]"></span>
            </span>
            Pricing Plans
          </motion.div>
          <h1 className="text-[35px] md:text-[40px] leading-[41px] font-extrabold  mb-[10px] font-['DM_Sans',sans-serif] text-[#0F172A]">
            Choose your perfect plan
          </h1>
          <p className="text-base text-[#475569] max-w-xl mx-auto font-medium ">
            Scale your business with powerful tools designed for growth — simple, transparent pricing.
          </p>
        </motion.div>

        {/* Error Alert */}
        <AnimatePresence>
          {err && (
            <motion.div
              initial={{ opacity: 0, height: 0, y: -10 }}
              animate={{ opacity: 1, height: 'auto', y: 0 }}
              exit={{ opacity: 0, height: 0, y: -10 }}
              className="max-w-xl mx-auto mb-6 overflow-hidden"
            >
              <div className="rounded-2xl border border-red-200 bg-red-50/80 backdrop-blur-md text-red-600 text-sm px-5 py-3 flex items-center justify-center shadow-sm">
                {err}
              </div>
            </motion.div>
          )}
          {msg && (
            <motion.div
              initial={{ opacity: 0, height: 0, y: -10 }}
              animate={{ opacity: 1, height: 'auto', y: 0 }}
              exit={{ opacity: 0, height: 0, y: -10 }}
              className="max-w-xl mx-auto mb-6 overflow-hidden"
            >
              <div className="rounded-2xl border border-green-200 bg-green-50/80 backdrop-blur-md text-green-700 text-sm px-5 py-3 flex items-center justify-center shadow-sm">
                {msg}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Billing Toggle */}
        <div className="mb-[24px]">
          <BillingToggle cycle={cycle} setCycle={setCycle} />
        </div>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20 space-y-5">
            <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }}>
              <Loader2 className="h-10 w-10 text-[#F97316]" />
            </motion.div>
            <p className="text-[#475569] font-medium animate-pulse">Loading amazing plans...</p>
          </div>
        ) : plans.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-20 bg-white rounded-2xl border border-[#E2E8F0] shadow-sm  mx-auto"
          >
            <p className="text-[#475569] text-lg font-medium">
              No {cycle} plans are available right now.
            </p>
          </motion.div>
        ) : (
          <>
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-4  xl:grid-cols-3 gap-4 lg:gap-6 items-start 2xl:max-w-[1320px] mx-auto relative z-10"
            >
              {plans.map((plan, index) => {
                const isCurrent = isActive && currentPlanId === plan.id;

                // Best value logic: prefer "Pro" plan, or the second plan if there's >= 3 plans, 
                // avoiding Free plan (price === 0) for most popular badge.
                const isFeatured = plan.name.toLowerCase().includes('pro')
                  || (!plans.some(p => p.name.toLowerCase().includes('pro')) && plans.length >= 3 && index === 1 && plan.price > 0)
                  || (!plans.some(p => p.name.toLowerCase().includes('pro')) && plans.length < 3 && index === plans.length - 1 && plan.price > 0);

                return (
                  <motion.div key={plan.id} variants={cardVariants} className="h-full flex">
                    <PricingCard
                      plan={plan}
                      cycle={cycle}
                      isActive={isCurrent}
                      isFeatured={isFeatured}
                      isSubscribing={subscribingId === plan.id}
                      onChoose={handleChoose}
                    />
                  </motion.div>
                );
              })}
            </motion.div>

            {/* Trust strip — mind-blowing polish ke liye */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 mt-12 pt-8 border-t border-gray-200/70 max-w-3xl mx-auto"
            >
              <span className="flex items-center gap-2 text-xs font-semibold text-[#94A3B8]">
                <ShieldCheck className="h-4 w-4 text-[#ED5A29]" /> Secure checkout via Shopify
              </span>
              <span className="flex items-center gap-2 text-xs font-semibold text-[#94A3B8]">
                <Clock className="h-4 w-4 text-[#ED5A29]" /> Cancel anytime
              </span>
              <span className="flex items-center gap-2 text-xs font-semibold text-[#94A3B8]">
                <Sparkles className="h-4 w-4 text-[#ED5A29]" /> Instant activation
              </span>
            </motion.div>
          </>
        )}
      </div>
    </div>
  );
}